import { X } from 'lucide-react';
import { debounce } from 'lodash';
import { useTransition } from 'react';

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectLabel,
  SelectSeparator,
} from '@/components/ui/select';
import { InputSearch } from 'src/components/ui/input-search';
import { Button } from 'src/components/ui/button';
import { CredentialTypeEnum } from 'src/graphql/schema';
import { credentialTypeOptions as options } from 'src/containers/Credentials';

const CredentialTypeSelect = ({
  onChange,
  credentialType,
}: {
  onChange: (value?: CredentialTypeEnum) => void;
  credentialType?: CredentialTypeEnum;
}) => {
  const onValueChange = (value: string) => onChange(value !== 'all' ? (value as CredentialTypeEnum) : undefined);

  return (
    <Select onValueChange={onValueChange} value={credentialType ?? 'all'}>
      <SelectTrigger className="w-10 sm:w-50 *:data-[slot=select-value]:hidden sm:*:data-[slot=select-value]:block">
        <SelectValue placeholder="Deposit status" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All</SelectItem>
        <SelectGroup>
          <SelectSeparator />
          <SelectLabel>Credential type</SelectLabel>
          {options.map(({ value, label }) => (
            <SelectItem value={value} key={value}>
              {label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

type Props = {
  query?: string;
  credentialType?: CredentialTypeEnum;
  setCredentialTypeFilter: (value?: CredentialTypeEnum) => void;
  setQueryFilter: (value?: string) => void;
  setInitialFilters: () => void;
};

const CredentialsFilters = ({
  query,
  credentialType,
  setCredentialTypeFilter,
  setQueryFilter,
  setInitialFilters,
}: Props) => {
  const [isPending, startTransition] = useTransition();

  const onChange = debounce((e) => startTransition(() => setQueryFilter(e.target.value)), 500);

  const onClearSearch = () => startTransition(() => setQueryFilter(undefined));

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <CredentialTypeSelect onChange={setCredentialTypeFilter} credentialType={credentialType} />
        <InputSearch
          placeholder="Search"
          className="max-w-[280px] w-full"
          onChange={onChange}
          isLoading={isPending}
          onClear={onClearSearch}
        />
      </div>
      {[credentialType, query].some(Boolean) && (
        <Button variant="ghost" className="px-0" onClick={setInitialFilters}>
          <X /> Clear all
        </Button>
      )}
    </div>
  );
};

export default CredentialsFilters;
